import { alpha, Box, ButtonBase, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { FlatRowSelectOptionList } from "web_myplann/Shared/Components/FlatRow/FlatRowSelectOptionList";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";

export type FlatRowSelectOption<TValue extends string | number = string> = {
	value: TValue;
	label: string;
};

export type FlatRowSelectProps<TValue extends string | number = string> = {
	label: string;
	value: TValue | "";
	options: FlatRowSelectOption<TValue>[];
	onChange: (value: TValue) => void;
	onBlur?: () => void;
	drawerTitle?: string;
	isLast?: boolean;
	error?: boolean;
	helperText?: string;
};

export const FlatRowSelect = <TValue extends string | number = string>({
	label,
	value,
	options,
	onChange,
	onBlur,
	drawerTitle,
	isLast,
	error,
	helperText,
}: FlatRowSelectProps<TValue>) => {
	const theme = useTheme();
	const [open, setOpen] = useState(false);

	const selectedLabel =
		options.find((option) => String(option.value) === String(value))?.label ??
		"";

	const handleSelect = (optionValue: TValue) => {
		onChange(optionValue);
		setOpen(false);
	};

	const closeDrawer = () => {
		setOpen(false);
		onBlur?.();
	};

	return (
		<Box>
			<ButtonBase
				onClick={() => setOpen(true)}
				sx={{
					width: "100%",
					px: 2,
					minHeight: 48,
					justifyContent: "space-between",
					borderBottom: isLast
						? "none"
						: `1px solid ${alpha(theme.palette.deprecated.grey[100], 0.6)}`,
					"&:active": { bgcolor: theme.palette.deprecated.grey[50] },
				}}
			>
				<Typography
					variant="deprecatedBody1"
					color={
						error
							? theme.palette.deprecated.error.main
							: theme.palette.deprecated.grey[500]
					}
					flexShrink={0}
					mr={2}
				>
					{label}
				</Typography>
				<Typography
					variant="deprecatedBodyMedium"
					color={theme.palette.primary.main}
					sx={{
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
				>
					{selectedLabel}
				</Typography>
			</ButtonBase>

			{error && helperText && (
				<Typography
					variant="deprecatedCaption"
					color={theme.palette.deprecated.error.main}
					px={2}
					pb={0.6}
				>
					{helperText}
				</Typography>
			)}

			<MyPlannDrawer
				anchor="bottom"
				open={open}
				onClose={closeDrawer}
				title={drawerTitle ?? label}
				closeButton
			>
				<FlatRowSelectOptionList
					options={options}
					value={value}
					onSelect={handleSelect}
				/>
			</MyPlannDrawer>
		</Box>
	);
};
